import api from "./api";
import * as db from "./offlineDb";

// Replays queued offline writes (captures + tasks) once back online, in the
// order they were made. Conflict rule per product decision: whichever edit
// was actually saved LAST (by real-world time) wins - so for updates, the
// server's current `updated_at` is compared against the timestamp captured
// when the offline edit was made; if the server has something newer (e.g.
// edited from another device while this one was offline), the server version
// wins and the queued local edit is dropped instead of clobbering it.
const ENTITY_STORE = { captures: "captures", tasks: "tasks" };

let flushing = false;
const listeners = new Set();

export function onSyncStateChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

async function notify() {
  const pending = (await db.getMutations()).length;
  listeners.forEach((cb) => cb({ pending, syncing: flushing }));
}

export async function getPendingCount() {
  return (await db.getMutations()).length;
}

async function remapTempId(store, tempId, realId) {
  const mutations = await db.getMutations();
  for (const m of mutations) {
    if (m.entityId === tempId) {
      const updated = { ...m, entityId: realId };
      if (updated.resourceUrl) updated.resourceUrl = updated.resourceUrl.replace(tempId, realId);
      await db.updateMutation(updated);
    }
  }
}

async function applyMutation(mutation) {
  const { entity, method, entityId, tempId, payload, clientTimestamp, collectionUrl, resourceUrl } = mutation;
  const store = ENTITY_STORE[entity];
  try {
    if (method === "POST") {
      const { data } = await api.post(collectionUrl, payload);
      await db.deleteOne(store, tempId);
      await db.putOne(store, { ...data, cachedAt: Date.now() });
      await remapTempId(store, tempId, data.id);
      window.dispatchEvent(new CustomEvent("offline-sync-remap", { detail: { store, tempId, realId: data.id } }));
    } else if (method === "PATCH") {
      let serverItem;
      try {
        const res = await api.get(resourceUrl);
        serverItem = res.data;
      } catch (e) {
        if (e.response?.status === 404) return; // deleted server-side in the meantime, nothing left to patch
        throw e;
      }
      const serverUpdatedAt = new Date(serverItem.updated_at || 0).getTime();
      if (serverUpdatedAt > clientTimestamp) {
        await db.putOne(store, { ...serverItem, cachedAt: Date.now() });
        window.dispatchEvent(new CustomEvent("offline-sync-conflict", { detail: { store, id: entityId } }));
        return;
      }
      const { data } = await api.patch(resourceUrl, payload);
      await db.putOne(store, { ...data, cachedAt: Date.now() });
    } else if (method === "DELETE") {
      try {
        await api.delete(resourceUrl);
      } catch (e) {
        if (e.response?.status !== 404) throw e;
      }
      await db.deleteOne(store, entityId);
    } else if (method === "PUT") {
      await api.put(resourceUrl, payload);
    }
  } catch (e) {
    if (!e.response) e.isNetworkError = true;
    throw e;
  }
}

export async function flushMutationQueue() {
  if (flushing || !navigator.onLine) return;
  flushing = true;
  await notify();
  try {
    const mutations = (await db.getMutations()).sort((a, b) => a.seq - b.seq);
    for (const mutation of mutations) {
      if (!navigator.onLine) break;
      try {
        await applyMutation(mutation);
        await db.removeMutation(mutation.seq);
      } catch (e) {
        if (e.isNetworkError) break; // still offline/flaky - stop, retry later
        await db.removeMutation(mutation.seq); // non-network failure, nothing more we can do with it
      }
    }
  } finally {
    flushing = false;
    await notify();
  }
}

let initialized = false;
export function initSyncEngine() {
  if (initialized) return;
  initialized = true;
  window.addEventListener("online", flushMutationQueue);
  if (navigator.onLine) flushMutationQueue();
  // Safety net in case the browser's `online` event doesn't fire reliably
  // (e.g. behind a flaky captive-portal-like connection).
  setInterval(() => {
    if (navigator.onLine) flushMutationQueue();
  }, 30000);
}
